/**
 * Forecast & Early Warnings, across the portfolio.
 *
 * Project Control answers "when does this project finish". This screen answers
 * the portfolio question: which projects have moved away from what was
 * committed, which capacity limits are projected to bite first, and what the
 * consequence is of nobody intervening.
 */
import { Link } from 'react-router-dom';
import type { ProjectTile } from '@dcc/shared';
import { api } from '../lib/api';
import { useProjection } from '../hooks/useProjection';
import { useLive } from '../hooks/useLive';
import { Confidence, Panel, Stat, StateChip, Unknown } from '../components/Primitives';
import {
  DegradedBanner,
  EmptyState,
  ErrorState,
  LiveBadge,
  SkeletonPanel,
} from '../components/States';
import { absoluteTime, duration, relativeTime } from '../lib/format';
import { toneFor } from '../lib/tone';

export function Forecast() {
  const projection = useProjection(() => api.portfolio(), []);
  const live = useLive(['portfolio.updated', 'capacity.updated'], projection.reload);

  if (projection.loading) return <SkeletonPanel rows={6} />;
  if (projection.error) return <ErrorState error={projection.error} onRetry={projection.reload} />;
  const view = projection.data!;

  // A project that has not started has no forecast by definition. Listing it as
  // an untrustworthy forecast would bury the projects whose numbers really are
  // shaky.
  const started = view.projects.filter(
    (tile) => tile.status !== 'DRAFT' && tile.status !== 'ONBOARDING',
  );
  const notStarted = view.projects.filter(
    (tile) => tile.status === 'DRAFT' || tile.status === 'ONBOARDING',
  );
  const forecastable = started.filter((tile) => tile.forecast.etaAt !== null);
  const unforecastable = started.filter((tile) => tile.forecast.etaAt === null);
  const slipping = forecastable
    .filter((tile) => (tile.forecast.deltaVsOriginalHours ?? 0) > 0)
    .sort(
      (a, b) => (b.forecast.deltaVsOriginalHours ?? 0) - (a.forecast.deltaVsOriginalHours ?? 0),
    );
  const lowConfidence = forecastable.filter(
    (tile) => tile.forecast.confidence === 'LOW' || tile.forecast.confidence === 'UNKNOWN',
  );

  const capacityWarnings = [
    ...view.crew
      .filter(
        (member) =>
          member.sessionRange.state === 'PROJECTED_SHORT' ||
          member.sessionRange.state === 'EXHAUSTED' ||
          member.weeklyFuel.state === 'PROJECTED_SHORT' ||
          member.weeklyFuel.state === 'EXHAUSTED',
      )
      .map((member) => ({
        id: member.id,
        who: member.name,
        layer:
          member.sessionRange.state === 'EXHAUSTED' ||
          member.sessionRange.state === 'PROJECTED_SHORT'
            ? ('session range' as const)
            : ('weekly fuel' as const),
        state:
          member.sessionRange.state === 'EXHAUSTED' ||
          member.sessionRange.state === 'PROJECTED_SHORT'
            ? member.sessionRange.state
            : member.weeklyFuel.state,
        at:
          member.sessionRange.projectedExhaustionAt ??
          member.weeklyFuel.projectedExhaustionAt ??
          member.sessionRange.resetsAt,
        resetsAt: member.sessionRange.resetsAt ?? member.weeklyFuel.resetsAt,
      })),
  ].sort((a, b) => (a.at ?? '9999').localeCompare(b.at ?? '9999'));

  return (
    <>
      <header className="topstrip">
        <div>
          <div className="label">{view.tenant.name}</div>
          <h1 className="topstrip__title">Forecast &amp; early warnings</h1>
          <p className="small dim">
            Every estimate here is derived from recorded inputs and carries the reasons behind its
            confidence. Where an input is missing, the answer is that it is unknown.
          </p>
        </div>
        <div className="topstrip__meta">
          <LiveBadge connection={live.connection} lastEventAt={live.lastEventAt} />
        </div>
      </header>

      {projection.degraded && (
        <DegradedBanner onRetry={projection.reload} loadedAt={projection.loadedAt} />
      )}

      <Panel
        title="Moved away from the original commitment"
        subtitle="Sorted by how far the current forecast has slipped past what was originally planned."
      >
        {slipping.length === 0 ? (
          <EmptyState
            tone="success"
            title="Nothing has slipped"
            body="No project's current forecast is later than the target it was committed to — for the projects where an original target was recorded."
          />
        ) : (
          <div className="rows">
            {slipping.map((tile) => (
              <ForecastRow key={tile.id} tile={tile} />
            ))}
          </div>
        )}
      </Panel>

      <div className="grid grid--sidebar">
        <Panel
          title="Projected capacity limits"
          subtitle="Which limit bites first, and what happens when it does. A limit is a stop, not a fault."
        >
          {capacityWarnings.length === 0 ? (
            <EmptyState
              tone="success"
              title="No limit is projected to bite"
              body="Every seat that reported usable telemetry reaches its next reset with capacity to spare."
            />
          ) : (
            <div className="rows">
              {capacityWarnings.map((warning) => (
                <div
                  key={`${warning.id}-${warning.layer}`}
                  className="row"
                  style={{ gridTemplateColumns: '1fr auto' }}
                  data-tone={toneFor('capacity', warning.state)}
                >
                  <div className="row__stack row__cell" data-label="Limit">
                    <strong>
                      {warning.who} · {warning.layer}
                    </strong>
                    <span className="row__sub">
                      {warning.state === 'EXHAUSTED'
                        ? 'Already spent. Work on this seat is stopped until it resets.'
                        : 'Projected to run out before the reset at the current burn.'}
                    </span>
                    <span className="row__sub">
                      Hand the work to another authorised colleague, or wait. Never share a seat.
                    </span>
                  </div>
                  <div
                    className="row__stack row__cell"
                    data-label="When"
                    style={{ alignItems: 'flex-end' }}
                  >
                    <StateChip kind="capacity" state={warning.state} />
                    <span className="row__sub">
                      {warning.at ? relativeTime(warning.at) : <Unknown />}
                    </span>
                    <span className="row__sub">
                      resets {warning.resetsAt ? relativeTime(warning.resetsAt) : 'unknown'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel
          title="Fleet capacity"
          subtitle="Burn against accepted throughput over the last window."
        >
          <div className="stack">
            <Stat
              label="Burn per completed journey"
              large
              value={
                view.fleet.burnPerCompletedJourney === null ? (
                  <Unknown />
                ) : (
                  `${view.fleet.burnPerCompletedJourney}%`
                )
              }
              note="The primary KPI. Lower is better; more agents does not move it on its own."
            />
            <div className="divider" />
            <Stat
              label="Team range"
              value={
                view.teamRange.totalAgentHours === null ? (
                  <Unknown />
                ) : (
                  `${view.teamRange.totalAgentHours} agent-h`
                )
              }
              note={
                view.teamRange.requiredAgentHours === null
                  ? 'No comparable workload estimate.'
                  : `against ${duration(view.teamRange.requiredAgentHours)} of remaining work`
              }
              tone={toneFor('capacity', view.teamRange.state)}
            />
            <StateChip kind="capacity" state={view.teamRange.state} />
          </div>
        </Panel>
      </div>

      <Panel
        title="Forecasts DCC will not stand behind"
        subtitle="Low or unknown confidence, with the reason. These are the numbers not to plan around."
      >
        {lowConfidence.length === 0 && unforecastable.length === 0 ? (
          <EmptyState
            tone="success"
            title="Every forecast is well supported"
            body="No started project's estimate rests on stale, missing or blocked inputs."
          />
        ) : (
          <div className="stack">
            {[...lowConfidence, ...unforecastable].map((tile) => (
              <div key={tile.id} className="card card--toned" data-tone="risk">
                <div className="spread">
                  <strong>
                    <Link to={`/projects/${tile.id}/control`}>{tile.name}</Link>
                  </strong>
                  <Confidence
                    confidence={tile.forecast.confidence}
                    percentValue={tile.forecast.confidencePercent}
                  />
                </div>
                <ul
                  className="small stack stack--tight"
                  style={{ margin: '8px 0 0', paddingLeft: 16 }}
                >
                  {tile.forecast.reasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {notStarted.length > 0 && (
        <Panel
          title="Not started yet"
          subtitle="These have no forecast because there is nothing to forecast from — not because their estimate is weak."
        >
          <div className="rows">
            {notStarted.map((tile) => (
              <div key={tile.id} className="row" style={{ gridTemplateColumns: '1fr auto' }}>
                <div className="row__stack row__cell" data-label="Project">
                  <strong>
                    <Link to={`/projects/${tile.id}/control`}>{tile.name}</Link>
                  </strong>
                  <span className="row__sub">{tile.connectionDetail}</span>
                </div>
                <span className="row__cell" data-label="Status">
                  <StateChip kind="project" state={tile.status} />
                </span>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <Panel
        title="If nobody intervenes"
        subtitle="The consequence of leaving every open item exactly as it is."
      >
        <ul className="stack stack--tight small" style={{ margin: 0, paddingLeft: 18 }}>
          {view.interventions.slice(0, 8).map((item) => (
            <li key={item.id}>
              <strong>{item.title}</strong> — {item.impact}{' '}
              {item.dueAt && Date.parse(item.dueAt) < Date.now() ? (
                <span style={{ color: 'var(--risk)' }}>Already past its due time.</span>
              ) : null}
            </li>
          ))}
          {view.interventions.length === 0 && <li>Nothing open degrades on its own.</li>}
        </ul>
      </Panel>
    </>
  );
}

function ForecastRow({ tile }: { tile: ProjectTile }) {
  const slip = tile.forecast.deltaVsOriginalHours ?? 0;
  return (
    <div className="row" style={{ gridTemplateColumns: '1.2fr 1fr 1fr auto' }} data-tone="risk">
      <div className="row__stack row__cell" data-label="Project">
        <strong>
          <Link to={`/projects/${tile.id}/control`}>{tile.name}</Link>
        </strong>
        <span className="row__sub">
          {tile.versionKey} · {tile.statusReason}
        </span>
      </div>
      <div className="row__stack row__cell" data-label="Current forecast">
        <span>{tile.forecast.etaAt ? relativeTime(tile.forecast.etaAt) : <Unknown />}</span>
        <span className="row__sub">
          {tile.forecast.etaAt ? absoluteTime(tile.forecast.etaAt) : ''}
        </span>
      </div>
      <div className="row__stack row__cell" data-label="Change against plan">
        <span style={{ color: 'var(--risk)' }}>{duration(slip)} later</span>
        <span className="row__sub">
          {tile.forecast.drivers
            .map((driver) => `${driver.label} ${duration(driver.hours)}`)
            .join(' · ')}
        </span>
      </div>
      <div className="row__cell" data-label="Confidence">
        <Confidence
          confidence={tile.forecast.confidence}
          percentValue={tile.forecast.confidencePercent}
          reasons={tile.forecast.reasons}
        />
      </div>
    </div>
  );
}
